import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import type { RootState } from "../../store/store";
import { affiliatesService, type AffiliatePrompt } from "../../api/services/affiliates.service";
import { Button, Modal, formatCurrency } from "../ui";
import EscrowChoiceModal from "../escrow/EscrowChoiceModal";

const PROMPTS_KEY = ["affiliates", "prompts"];

interface PromptCopy {
  title: string;
  body: React.ReactNode;
  note?: string;
  /** Yes / No service offer, or a reminder that is only acknowledged */
  service: boolean;
  /** Service offers: who a Yes shares the member's name and email with */
  sharesWith?: string;
}

/** Pop-up wording per trigger (Master §9.6, FR-03b); escrow_choice has its own modal */
function promptCopy(prompt: AffiliatePrompt): PromptCopy {
  const who = prompt.affiliateName || null;
  switch (prompt.kind) {
    case "title_prelim":
      return {
        title: "Want me to send a prelim you can share with buyers?",
        body: (
          <>
            {who ? <strong>{who}</strong> : "Your title partner"} can prepare a preliminary title report for{" "}
            {prompt.listingAddress}.
          </>
        ),
        note: "They've been told about your listing either way and may reach out directly.",
        service: true,
        sharesWith: who ?? "our title partner",
      };
    case "lender_loan":
      return {
        title: "Want me to start the loan?",
        body: (
          <>
            Your offer on {prompt.listingAddress} was accepted. {who ? <strong>{who}</strong> : "Your lender"} can start
            the loan now.
          </>
        ),
        note: "They've been told about your accepted offer either way and may reach out directly.",
        service: true,
        sharesWith: who ?? "your lender",
      };
    case "agreement_relist":
      return {
        title: "Your offer was accepted",
        body: (
          <>
            Reminder: you agreed to give {who ? <strong>{who}</strong> : "the agent who invited you"} the listing when
            you sell {prompt.listingAddress}.
          </>
        ),
        service: false,
      };
    case "agreement_fee":
      return {
        title: "Your offer was accepted",
        body: (
          <>
            Reminder: you agreed to pay {who ? <strong>{who}</strong> : "the agent who invited you"}
            {prompt.feeAmount ? <> a fee of <strong>{formatCurrency(prompt.feeAmount)}</strong></> : " a fee"} for{" "}
            {prompt.listingAddress}.
          </>
        ),
        note: "This is between you and the agent, paid outside FlipIQ Marketplace. It isn't part of escrow.",
        service: false,
      };
    case "escrow_choice":
      return { title: "", body: null, service: false };
  }
}

/**
 * Epic 8 pop-ups: one in-app modal at a time, shown at the trigger moment and again after a refresh
 * until answered. Closing it counts as the answer ("No" / seen), so each pop-up is seen once.
 */
export const AffiliatePromptHost: React.FC = () => {
  const signedIn = useSelector((state: RootState) => !!state.auth.id);
  const queryClient = useQueryClient();
  const { data: prompts = [] } = useQuery({
    queryKey: PROMPTS_KEY,
    queryFn: () => affiliatesService.prompts(),
    enabled: signedIn,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    retry: false,
  });
  const answer = useMutation({
    mutationFn: ({ id, value }: { id: string; value: "yes" | "no" | "seen" }) => affiliatesService.answer(id, value),
    onSettled: () => queryClient.invalidateQueries({ queryKey: PROMPTS_KEY }),
  });

  // PRD 9.1: "Decide later" hides the escrow pop-up until the next visit (it stays pending)
  const [deferred, setDeferred] = useState<string[]>([]);
  const prompt = prompts.find((p) => !deferred.includes(p.id));
  if (!prompt) return null;
  if (prompt.kind === "escrow_choice") {
    return (
      <EscrowChoiceModal
        prompt={prompt}
        onDone={() => {
          queryClient.setQueryData<AffiliatePrompt[]>(PROMPTS_KEY, (old) => (old ?? []).filter((p) => p.id !== prompt.id));
          void queryClient.invalidateQueries({ queryKey: PROMPTS_KEY });
        }}
        onLater={() => setDeferred((d) => [...d, prompt.id])}
      />
    );
  }
  const copy = promptCopy(prompt);
  const reply = (value: "yes" | "no" | "seen") => {
    queryClient.setQueryData<AffiliatePrompt[]>(PROMPTS_KEY, (old) => (old ?? []).filter((p) => p.id !== prompt.id));
    answer.mutate({ id: prompt.id, value });
  };

  return (
    <Modal
      open
      onClose={() => reply(copy.service ? "no" : "seen")}
      title={copy.title}
      maxWidth="max-w-md"
      footer={
        copy.service ? (
          <div className="space-y-2">
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => reply("no")}>
                No thanks
              </Button>
              <Button variant="brand" onClick={() => reply("yes")}>
                Yes
              </Button>
            </div>
            <p className="text-right text-meta text-gray-500" data-testid="affiliate-prompt-consent">
              Yes shares your name and email with {copy.sharesWith}.
            </p>
          </div>
        ) : (
          <div className="flex justify-end">
            <Button variant="primary" onClick={() => reply("seen")}>
              Got it
            </Button>
          </div>
        )
      }
    >
      <div className="space-y-3 text-sm text-gray-700" data-testid={`affiliate-prompt-${prompt.kind}`}>
        <p>{copy.body}</p>
        {copy.note && <p className="text-meta text-gray-500">{copy.note}</p>}
        <p>
          <Link to={`/buy/${prompt.listingId}`} className="font-medium text-brand-700 hover:underline">
            View property
          </Link>
        </p>
      </div>
    </Modal>
  );
};

export default AffiliatePromptHost;
